import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useOverheadCategoryImpact,
  useOverheadSubcategoryImpact,
} from "@/hooks/use-overhead-categories";

interface DeactivateOverheadEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "category" | "subcategory";
  entityId: string;
  entityName: string;
  isPending: boolean;
  onConfirm: () => void;
}

export function DeactivateOverheadEntityDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  entityName,
  isPending,
  onConfirm,
}: DeactivateOverheadEntityDialogProps) {
  const categoryImpact = useOverheadCategoryImpact(entityId, open && entityType === "category");
  const subcategoryImpact = useOverheadSubcategoryImpact(
    entityId,
    open && entityType === "subcategory",
  );

  const impact = entityType === "category" ? categoryImpact : subcategoryImpact;
  const { data, isLoading } = impact;

  const hasOpenEntries = (data?.currentWeekEntries ?? 0) > 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate {entityType}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>
              <p>
                Deactivating <strong className="text-foreground">{entityName}</strong> will hide it
                from engineers going forward.
                {entityType === "category" && (
                  <> All active subcategories will also be deactivated.</>
                )}
              </p>

              {isLoading && <p className="mt-3 text-sm text-muted-foreground">Checking impact…</p>}

              {data && data.currentWeekEntries > 0 && (
                <div className="callout callout--warn mt-3">
                  <AlertTriangle className="callout__icon w-4 h-4" />
                  <div className="text-sm">
                    <strong className="callout__title">Open entries this week</strong>
                    <p>
                      {data.currentWeekEntries} unsubmitted time{" "}
                      {data.currentWeekEntries === 1 ? "entry" : "entries"} in the current week
                      reference this — deactivation is blocked until those entries are submitted or
                      approved.
                    </p>
                  </div>
                </div>
              )}

              {data && data.totalEntries > 0 && data.currentWeekEntries === 0 && (
                <p className="mt-3 text-sm text-muted-foreground">
                  {data.totalEntries} historical time{" "}
                  {data.totalEntries === 1 ? "entry" : "entries"} reference this — they will not be
                  affected.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isPending || isLoading || hasOpenEntries}
          >
            {isPending ? "Deactivating..." : "Deactivate"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
